import React, { useEffect, useState } from 'react';
import {useTranslation} from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Loader from '../../modules/layout/components/Loader';
import SortDirection from '../../modules/layout/components/SortDirection';
import AppBreadcrumb from '../../modules/layout/components/AppBreadcrumb';
import Pagination from '../../modules/layout/components/Pagination';
import {useToast} from '../../modules/layout/components/ToastContext';
import { getAll, post } from '../_request';
import BaseSelect from '../../modules/layout/components/Select/BaseSelect';

type KeycloakClient = {
  id: string;
  name?: string;
  clientId?: string;
  attributes?: Record<string, any>;
  login_options?: string | null;
};

const optionKeys = ['citizen', 'business', 'entra-id', 'certificate', 'admin-module'];

export default function ClientsList() {
  const {t} = useTranslation();
  const {showToast} = useToast();
  const navigate = useNavigate();
  const [clients, setClients] = useState<KeycloakClient[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [originalSelected, setOriginalSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchField, setSearchField] = useState<'both' | 'name' | 'clientId'>('both');
  const [sortField, setSortField] = useState<'name' | 'clientId'>('name');
  const [asc, setAsc] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const searchOptions = React.useMemo(() => [
    { value: 'both', label: `${t('Name')} / ${t('ClientId')}` },
    { value: 'name', label: t('Name') },
    { value: 'clientId', label: t('ClientId') },
  ], [t]);

  const handleSort = (field: 'name' | 'clientId') => {
    if (sortField === field) {
      setAsc(!asc);
    } else {
      setSortField(field);
      setAsc(true);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, searchField]);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  async function fetchClients() {
    try {
      setLoading(true);
      const result = await getAll('/api/Clients/getall', '');
      const respStatus = (result as any)?.status || (result as any)?.response?.status;

      if (respStatus === 200) {
      const payload = result?.data?.Data ?? result?.data?.data ?? result?.data;
      const data = Array.isArray(payload) ? payload : [];

      setClients(data);

      const initial: Record<string, boolean> = {};
      data.forEach((c: KeycloakClient) => {
        const clientKey = c.clientId ?? c.id;
        const existing = (c.login_options || c.attributes?.login_options || '').toString();
          const opts = existing.split(',').map((x: string) => x.trim()).filter(Boolean);

        if (opts.length === 0) {
          optionKeys.forEach(opt => {
            initial[`${clientKey}_${opt}`] = true;
          });
        } else {
          optionKeys.forEach(opt => {
            initial[`${clientKey}_${opt}`] = opts.includes(opt);
          });
        }
      });

      setSelected(initial);
      setOriginalSelected(initial);
      setLoading(false);
      } else if (respStatus === 403) {
        navigate('/error/403');
      } else if (respStatus === 401) {
        navigate('/auth/login');
      } else if (respStatus === 500) {
        navigate('/error/500');
      } else {
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }
    } catch (error) {
      setLoading(false);
      console.log(t('ConnectionError', { error: String(error) }));
      showToast(t('ConnectionError', { error: String(error) }), 'error');
      navigate('/');
    }
  }

  function toggleOption(clientKey: string, option: string) {
    const key = `${clientKey}_${option}`;
    setSelected(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleAllForOption(option: string) {
    const allChecked = paged.every(client => {
      const clientKey = client.clientId ?? client.id;
      return selected[`${clientKey}_${option}`];
    });

    setSelected(prev => {
      const updated = { ...prev };
      paged.forEach(client => {
        const clientKey = client.clientId ?? client.id;
        updated[`${clientKey}_${option}`] = !allChecked;
      });
      return updated;
    });
  }

  function isAllCheckedForOption(option: string): boolean {
    if (paged.length === 0) return false;
    return paged.every(client => {
      const clientKey = client.clientId ?? client.id;
      return selected[`${clientKey}_${option}`];
    });
  }

  function isSomeCheckedForOption(option: string): boolean {
    return paged.some(client => {
      const clientKey = client.clientId ?? client.id;
      return selected[`${clientKey}_${option}`];
    });
  }

  function getModifiedClients(): KeycloakClient[] {
    return clients.filter(client => {
      const clientKey = client.clientId ?? client.id;
      return optionKeys.some(opt => {
        const key = `${clientKey}_${opt}`;
        return selected[key] !== originalSelected[key];
      });
    });
  }

  function getChangeSummary() {
    return getModifiedClients().map(client => {
      const clientKey = client.clientId ?? client.id;
      const displayName = client.name && client.name.includes('${') ? (client.clientId ?? '') : client.name ?? client.clientId;
      const changes: { option: string; from: boolean; to: boolean }[] = [];
      
      optionKeys.forEach(opt => {
        const key = `${clientKey}_${opt}`;
        if (selected[key] !== originalSelected[key]) {
          changes.push({
            option: opt,
            from: originalSelected[key],
            to: selected[key]
          });
        }
      });
      
      return {
        client,
        displayName,
        changes
      };
    });
  }

  function revertClientChanges(client: KeycloakClient) {
    const clientKey = client.clientId ?? client.id;
    setSelected(prev => {
      const updated = { ...prev };
      optionKeys.forEach(opt => {
        const key = `${clientKey}_${opt}`;
        updated[key] = originalSelected[key];
      });
      return updated;
    });
  }

  async function updateClient(client: KeycloakClient) {
    const clientKey = client.clientId ?? client.id;
    const selectedOptions = optionKeys.filter(opt => selected[`${clientKey}_${opt}`]);
    const loginOptions = selectedOptions.join(',');

    try {
      const params = `?clientId=${encodeURIComponent(client.clientId!)}&loginOptions=${encodeURIComponent(loginOptions)}`;
      const result = await post('/api/Clients/update-login-options' + params, null);
      const respStatus = (result as any)?.response?.status || (result as any)?.status;
      
      if (respStatus === 200) {
        const clientKey = client.clientId ?? client.id;
        const updated = { ...originalSelected };
        optionKeys.forEach(opt => {
          const key = `${clientKey}_${opt}`;
          updated[key] = selected[key];
        });
        setOriginalSelected(updated);
        showToast && showToast(t('ClientUpdatedSuccessfully'), 'success');
      } else if (respStatus === 403) {
        navigate('/error/403');
      } else if (respStatus === 401) {
        navigate('/auth/login');
      } else if (respStatus === 500) {
        navigate('/error/500');
      } else {
        showToast(t('StatusCode', { code: String(respStatus) }), 'error');
        throw new Error(t('StatusCode', { code: String(respStatus) }));
      }
    } catch (error) {
      console.log(t('ConnectionError', { error: String(error) }));
      showToast(t('ConnectionError', { error: String(error) }), 'error');
      navigate('/');
    }
  }

  async function updateAllModified() {
    const modifiedClients = getModifiedClients();
    if (modifiedClients.length === 0) return;

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const client of modifiedClients) {
      try {
        const clientKey = client.clientId ?? client.id;
        const selectedOptions = optionKeys.filter(opt => selected[`${clientKey}_${opt}`]);
        const loginOptions = selectedOptions.join(',');
        const params = `?clientId=${encodeURIComponent(client.clientId!)}&loginOptions=${encodeURIComponent(loginOptions)}`;
        const result = await post('/api/Clients/update-login-options' + params, null);
        const respStatus = (result as any)?.response?.status || (result as any)?.status;
        
        if (respStatus === 200) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    setLoading(false);

    if (successCount > 0) {
      setOriginalSelected({ ...selected });
      showToast(t('ClientsUpdatedSuccessfully', { count: successCount }), 'success');
    }
    
    if (errorCount > 0) {
      showToast(t('SomeUpdatesFailed', { count: errorCount }), 'error');
    }
  }

  const filtered = clients.filter(c => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = (c.name ?? '').toLowerCase();
    const cid = (c.clientId ?? '').toLowerCase();
    if (searchField === 'both') return name.includes(q) || cid.includes(q);
    if (searchField === 'name') return name.includes(q);
    return cid.includes(q);
  });

  const sorted = filtered.slice().sort((a, b) => {
    const getVal = (c: KeycloakClient, f: 'name' | 'clientId') => {
      if (f === 'name') return (c.name ?? c.clientId ?? '').toString();
      return (c.clientId ?? '').toString();
    };

    const va = getVal(a, sortField).toLowerCase();
    const vb = getVal(b, sortField).toLowerCase();
    const cmp = va.localeCompare(vb);
    return asc ? cmp : -cmp;
  });
  const totalItems = sorted.length;
  const startIndex = (page - 1) * pageSize;
  const paged = sorted.slice(startIndex, startIndex + pageSize);
  const modifiedCount = getModifiedClients().length;

  return (
    <div className="gg-clientList container mt-4">
      <AppBreadcrumb
        items={[
          { title: t('Home'), href: '/' },
          { title: t('Clients') }
        ]}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">{t('Clients')}</h3>
        {modifiedCount > 0 && (
          <button 
            className="btn btn-danger"
            onClick={() => setShowConfirmModal(true)}
            disabled={loading}
          >
            {t('SaveChanges')} ({modifiedCount})
          </button>
        )}
      </div>

      {loading && <Loader />}

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div className="d-flex w-50 align-items-center">
          <input className="form-control" placeholder={t('Search')} value={search} onChange={e => setSearch(e.target.value)} />
          <div className="ms-2 d-flex align-items-center">
            <label htmlFor="clients-searchby" className="me-2 mb-0" style={{ whiteSpace: 'nowrap' }}>{t('SearchBy')}</label>
            <div style={{ minWidth: 200 }}>
              <BaseSelect
                options={searchOptions}
                value={searchOptions.find(o => o.value === searchField) || null}
                onChange={(opt: any) => setSearchField(opt?.value || 'both')}
                isClearable={false}
                placeholder={`-- ${t('SelectOption')} --`}
                styles={{
                  control: (provided: any) => ({
                    ...provided,
                    minHeight: 'calc(1.5em + 0.75rem)',
                    boxShadow: 'none',
                    borderColor: '#ced4da',
                    '&:hover': { borderColor: '#ff0000' }
                  }),
                  singleValue: (provided: any) => ({ ...provided, color: 'inherit' }),
                  menu: (provided: any) => ({ ...provided, zIndex: 5 })
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>{t('Name')} <SortDirection isActive={sortField === 'name'} order={asc ? 'asc' : 'desc'} /></th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('clientId')}>{t('ClientId')} <SortDirection isActive={sortField === 'clientId'} order={asc ? 'asc' : 'desc'} /></th>
              {optionKeys.map(o => {
                const allChecked = isAllCheckedForOption(o);
                const someChecked = isSomeCheckedForOption(o);
                return (
                  <th
                    key={o}
                    className="text-center align-middle"
                    style={{ minWidth: 110, whiteSpace: 'nowrap' }}
                  >
                    <div className="d-flex flex-column align-items-center">
                      <div className="form-check mb-1">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          style={{ 
                            width: '20px', 
                            height: '20px', 
                            cursor: 'pointer'
                          }}
                          checked={allChecked}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = !allChecked && someChecked;
                            }
                          }}
                          onChange={() => toggleAllForOption(o)}
                          title={allChecked ? t('DeselectAll') : t('SelectAll')}
                        />
                      </div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t(`LoginTypes.${o}`)}</div>
                    </div>
                  </th>
                );
              })}
              <th>{t('Actions')}</th>
            </tr>
          </thead>
          <tbody className="table-group-divider">
            {paged.map(client => {
              const clientKey = client.clientId ?? client.id;
              const displayName = client.name && client.name.includes('${') ? (client.clientId ?? '') : client.name ?? client.clientId;
              return (
                <tr key={clientKey}>
                  <td>{displayName}</td>
                  <td>{client.clientId}</td>
                  {optionKeys.map(opt => {
                    const key = `${clientKey}_${opt}`;
                    return (
                      <td key={opt} className="text-center align-middle">
                        <div className="form-check d-flex justify-content-center m-0">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={Boolean(selected[key])}
                            onChange={() => toggleOption(clientKey, opt)}
                          />
                        </div>
                      </td>
                    );
                  })}
                  <td>
                    <button className="btn gg-btn-black btn-sm" onClick={() => updateClient(client)}>{t('Update')}</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
              <Pagination currentPage={page} onPageChange={(p) => setPage(p)} totalItems={totalItems} pageSize={pageSize} onPageSizeChange={handlePageSizeChange} />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('ConfirmChanges')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowConfirmModal(false)} aria-label="Close"></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  {t('AboutToUpdateClients', { count: modifiedCount })}
                </p>
                <div className="list-group">
                  {getChangeSummary().map(({ client, displayName, changes }) => {
                    const clientKey = client.clientId ?? client.id;
                    return (
                      <div key={clientKey} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-start">
                          <h6 className="mb-2 flex-grow-1">
                            <strong>{displayName}</strong>
                            {displayName !== client.clientId && (
                              <small className="text-muted ms-2">({client.clientId})</small>
                            )}
                          </h6>
                          <button
                            type="button"
                            className="btn-close"
                            aria-label="Remove"
                            title={t('RemoveFromBatch')}
                            onClick={() => {
                              revertClientChanges(client);
                              // Close modal if no more changes
                              setTimeout(() => {
                                if (getModifiedClients().length === 0) {
                                  setShowConfirmModal(false);
                                }
                              }, 0);
                            }}
                            style={{ fontSize: '0.75rem' }}
                          ></button>
                        </div>
                        <div className="ms-3">
                          {changes.map(({ option, from, to }) => (
                            <div key={option} className="mb-1">
                              <span className="badge bg-secondary me-2">{t(`LoginTypes.${option}`)}</span>
                              <span className={from ? 'text-success' : 'text-muted'}>✓</span>
                              <span className="mx-2">→</span>
                              <span className={to ? 'text-success' : 'text-danger'}>{to ? '✓' : '✗'}</span>
                              <small className="ms-2 text-muted">
                                {to ? t('Enabled') : t('Disabled')}
                              </small>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowConfirmModal(false)}>
                  {t('Cancel')}
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger" 
                  onClick={() => {
                    setShowConfirmModal(false);
                    updateAllModified();
                  }}
                >
                  {t('ConfirmAndSave')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
